import React, { useEffect, useState } from "react"
import { AccessColor, Area, AreaFormModalMetaProps, Volunteer } from "../data/types"
import { API_BASE_URL, LeadAssignModalMeta } from "../data"
import { Button, Modal } from "./ui"
import { DeleteIcon, EmailIcon, PersonIcon, PhoneIcon } from "../icons"
import { LeadAssignModal } from "./LeadAssignModal"


type Props = {
    meta: AreaFormModalMetaProps
    isOpen: boolean
    onClose: () => void
    onSubmit: (areaData: {
        name: string, leadName: string, leadPhoneNumber: string,
        deputyLeadName: string, deputyLeadPhoneNumber: string
    }) => Promise<void>
    initialArea?: Area | null
    canManageVisibility?: boolean
}


export const AreaFormModal = (props: Props) => {
    const { meta, isOpen, onClose, onSubmit, initialArea, canManageVisibility = false } = props

    const [formData, setFormData] = useState({
        name: '',
        leadName: '',
        leadPhoneNumber: '',
        deputyLeadName: '',
        deputyLeadPhoneNumber: '',
        required_volunteer_count: 0,
        lead_walkie_talkie: false,
        deputy_lead_walkie_talkie: false,
        shifts_visible: false
    })

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isLeadAssignModalOpen, setIsLeadAssignModalOpen] = useState(false);
    const [leadTab, setLeadTab] = useState<string | null>(null) // checks whether lead or deputy lead is being selected
    const [leadVolunteer, setLeadVolunteer] = useState<Volunteer | null>(null);
    const [deputyLeadVolunteer, setDeputyLeadVolunteer] = useState<Volunteer | null>(null);
    const [allAccessColors, setAllAccessColors] = useState<AccessColor[]>([])
    const [selectedAccessColor, setSelectedAccessColor] = useState<AccessColor | null>(null);




    const fetchAccessColors = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/access-color/`, { credentials: 'include' })

            if (!response.ok) throw new Error('Failed to fetch access colors.')

            const result = await response.json();

            setAllAccessColors(result)
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to fetch access colors.")
        }

        console.log("Fetch completed")

    }

    // Populate form when editing an area or reset when modal closes
    useEffect(() => {

        if (isOpen) fetchAccessColors()

        if (initialArea && isOpen) {
            setFormData({
                name: initialArea.name ?? '',
                leadName: initialArea.lead_name ?? '',
                leadPhoneNumber: initialArea.lead_phone_number ?? '',
                deputyLeadName: initialArea.deputy_lead_name ?? '',
                deputyLeadPhoneNumber: initialArea.deputy_lead_phone_number ?? '',
                required_volunteer_count: initialArea.required_volunteer_count ?? 0,
                lead_walkie_talkie: initialArea.lead ? initialArea.lead_walkie_talkie : false,
                deputy_lead_walkie_talkie: initialArea.deputy_lead ? initialArea.deputy_lead_walkie_talkie : false,
                shifts_visible: initialArea.shifts_visible ?? false
            })
            setLeadVolunteer(initialArea?.lead)
            setDeputyLeadVolunteer(initialArea.deputy_lead)

        } else if (!isOpen) {
            setFormData({
                name: '',
                leadName: '',
                leadPhoneNumber: '',
                deputyLeadName: '',
                deputyLeadPhoneNumber: '',
                required_volunteer_count: 0,
                lead_walkie_talkie: false,
                deputy_lead_walkie_talkie: false,
                shifts_visible: false
            })

            setLeadVolunteer(null)
            setDeputyLeadVolunteer(null)
        }

        setError(null)
    }, [initialArea, isOpen])


    useEffect(() => {
        if (initialArea?.access_color && isOpen) {
            setSelectedAccessColor(initialArea.access_color);
        }

        if (!isOpen) {
            setSelectedAccessColor(null);
        }
    }, [initialArea, isOpen]);


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)
        setError(null)

        try {

            const formattedData = {
                ...formData,
                lead_csid: leadVolunteer?.csid ?? null,
                deputy_lead_csid: deputyLeadVolunteer?.csid ?? null,
                access_color_id: selectedAccessColor?.id ?? null
            }

            await onSubmit(formattedData)
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setLoading(false)
        }
    }


    const handleLeadModalOpen = (tab: string) => {
        setLeadTab(tab)
        setIsLeadAssignModalOpen(true)
    }

    const handleLeadModalClose = () => {
        setLeadTab(null)
        setIsLeadAssignModalOpen(false)
    }

    const handleLeadModalSubmit = async (volunteer: Volunteer) => {
        leadTab === 'lead' ? setLeadVolunteer(volunteer) : setDeputyLeadVolunteer(volunteer)
    }

    const handleRemoveLead = (tab: string) => {
        tab === 'lead' ? setLeadVolunteer(null) : setDeputyLeadVolunteer(null)
    }

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={meta.title}
                maxWidth="md"
                footer={
                    <div className="flex gap-3 justify-end">
                        <Button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            variant="outline"
                            color="neutral"
                        >
                            {meta.cancelButton}
                        </Button>
                        <Button
                            type="submit"
                            form="area-form"
                            disabled={loading}
                        >
                            {loading ? meta.submittingButton : meta.submitButton}
                        </Button>
                    </div>
                }
            >
                <form id="area-form" onSubmit={handleSubmit}>
                    <div className="px-6 py-4">
                        {error && (
                            <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                                {error}
                            </div>
                        )}

                        <div className="mb-4">
                            <div className="space-y-2">
                                {/* Name Field*/}
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.nameLabel} <span className="text-danger-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    required
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                    placeholder={meta.namePlaceholder}
                                />

                                {/* <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {meta.required_volunteer_count_title}
                                    </label>
                                    <input
                                        type="number"
                                        id="required_volunteer_count"
                                        name="required_volunteer_count"
                                        value={formData.required_volunteer_count}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                        placeholder={"0"}
                                    />
                                </div> */}

                                <div className="w-full">

                                    <div className='flex justify-between'>
                                        <label
                                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                        >
                                            {meta.leadNameTitle}
                                        </label>
                                        {leadVolunteer && <Button variant='icon' size='sm' color='danger' onClick={() => handleRemoveLead('lead')}>
                                            <DeleteIcon className="w-4 h-4" />
                                        </Button>}
                                    </div>

                                    <div
                                        className="flex items-center justify-between w-full
                                                                rounded-md border border-gray-300 dark:border-gray-600
                                                                 bg-white dark:bg-gray-800
                                                                px-3 py-2"
                                    >
                                        {(leadVolunteer ?
                                            <div className="text-gray-900 dark:text-white">
                                                <div className="flex items-center gap-2">
                                                    <PersonIcon />
                                                    <p>{`${leadVolunteer.first_name} ${leadVolunteer.last_name}`}</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <EmailIcon />
                                                    <p>{`${leadVolunteer.email}`}</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <PhoneIcon />
                                                    <p>{`${leadVolunteer.phone}`}</p>
                                                </div>
                                            </div>
                                            :
                                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                                No Lead Allocated
                                            </span>)

                                        }

                                        <Button
                                            type="button"
                                            color="primary"
                                            size="sm"
                                            onClick={() => { handleLeadModalOpen('lead') }}
                                        >
                                            Select
                                        </Button>
                                    </div>

                                </div>

                                {leadVolunteer && (<div >
                                    <label className='text-md text-gray-600 dark:text-gray-300 me-2 italic'>{meta.require_walkie_talkie_label}</label>
                                    <input
                                        type="checkbox"
                                        name="lead_walkie_talkie"
                                        checked={formData.lead_walkie_talkie}
                                        onChange={(e) =>
                                            setFormData(prev => ({
                                                ...prev,
                                                lead_walkie_talkie: e.target.checked,
                                            }))
                                        }
                                    />
                                </div>)}


                                <div className="w-full">
                                    <div className='flex justify-between'>
                                        <label
                                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                        >
                                            {meta.deputyLeadNameTitle}
                                        </label>
                                        {deputyLeadVolunteer && <Button variant='icon' size='sm' color='danger' onClick={() => handleRemoveLead('deputy_lead')}>
                                            <DeleteIcon className="w-4 h-4" />
                                        </Button>}
                                    </div>

                                    <div
                                        className="flex items-center justify-between w-full
                                                                rounded-md border border-gray-300 dark:border-gray-600
                                                                 bg-white dark:bg-gray-800
                                                                px-3 py-2"
                                    >
                                        {(deputyLeadVolunteer ?
                                            <div className="text-gray-900 dark:text-white">
                                                <div className="flex items-center gap-2">
                                                    <PersonIcon />
                                                    <p>{`${deputyLeadVolunteer.first_name} ${deputyLeadVolunteer.last_name}`}</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <EmailIcon />
                                                    <p>{`${deputyLeadVolunteer.email}`}</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <PhoneIcon />
                                                    <p>{`${deputyLeadVolunteer.phone}`}</p>
                                                </div>
                                            </div>
                                            :
                                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                                No Deputy Lead Allocated
                                            </span>)
                                        }

                                        <Button
                                            type="button"
                                            color="primary"
                                            size="sm"
                                            onClick={() => { handleLeadModalOpen('deputy_lead') }}
                                        >
                                            Select
                                        </Button>
                                    </div>
                                </div>
                                {deputyLeadVolunteer && (<div className='mt-3'>
                                    <label className='text-md text-gray-600 dark:text-gray-300 me-2 italic'>{meta.require_walkie_talkie_label}</label>
                                    <input
                                        type="checkbox"
                                        name="lead_walkie_talkie"
                                        checked={formData.deputy_lead_walkie_talkie}
                                        onChange={(e) =>
                                            setFormData(prev => ({
                                                ...prev,
                                                deputy_lead_walkie_talkie: e.target.checked,
                                            }))
                                        }
                                    />
                                </div>)}

                                {/* Access Color */}

                                <div className="w-full">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {meta.accessColorLabel ?? 'Access Color'}
                                    </label>

                                    <select
                                        value={selectedAccessColor?.id ?? ''}
                                        onChange={(e) => {
                                            const selected = allAccessColors.find(
                                                c => c.id === Number(e.target.value)
                                            ) || null;
                                            setSelectedAccessColor(selected);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600
                                        rounded-md shadow-sm focus:outline-none focus:ring-2
                                        focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"

                                        style={{ backgroundColor: selectedAccessColor ? selectedAccessColor.color : "#FFFFFF" }}
                                    >
                                        <option value="">Select an access color</option>

                                        {allAccessColors.map((ac, index) => (
                                            <option key={index} value={ac.id} style={{ backgroundColor: ac.color }}>
                                                {ac.color}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Shifts Visible Toggle - only shown to admins */}
                                {canManageVisibility && (
                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50 mt-4">
                                        <div className="flex items-start gap-3">
                                            <input
                                                type="checkbox"
                                                id="shifts_visible"
                                                name="shifts_visible"
                                                checked={formData.shifts_visible}
                                                onChange={handleChange}
                                                className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                                            />
                                            <div>
                                                <label htmlFor="shifts_visible" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    {meta.shiftsVisibleLabel}
                                                </label>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                    {meta.shiftsVisibleDescription}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}



                                {/* <label htmlFor="lead-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.leadNameTitle}
                                </label>
                                <input
                                    type="text"
                                    id="lead-name"
                                    name="leadName"

                                    value={formData.leadName}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                    placeholder={meta.leadNamePlaceholder}
                                />


                                <label htmlFor="lead-phone-number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.leadcontactTitle}
                                </label>
                                <input
                                    type="text"
                                    id="lead-phone-number"
                                    name="leadPhoneNumber"

                                    value={formData.leadPhoneNumber}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                    placeholder={meta.leadPhoneNumberPlaceHolder}
                                />


                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.deputyLeadNameTitle}
                                </label>
                                <input
                                    type="text"
                                    id="deputy-lead-name"
                                    name="deputyLeadName"

                                    value={formData.deputyLeadName}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                    placeholder={meta.deputyLeadNamePlaceHolder}
                                />


                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.deputyLeadcontactTitle}
                                </label>
                                <input
                                    type="text"
                                    id="deputy-lead-phone-number"
                                    name="deputyLeadPhoneNumber"

                                    value={formData.deputyLeadPhoneNumber}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                    placeholder={meta.deputyLeadPhoneNumberPlaceHolder}
                                /> */}

                            </div>
                        </div>
                    </div>
                </form>
            </Modal>

            <LeadAssignModal
                meta={LeadAssignModalMeta}
                title={leadTab === 'lead' ? "Allocate Lead" : "Allocate Deputy Lead"}
                isOpen={isLeadAssignModalOpen}
                onClose={handleLeadModalClose}
                onSubmit={handleLeadModalSubmit}
                currentLead={leadTab === 'lead' ? leadVolunteer : deputyLeadVolunteer}
            />
        </>
    )
}
